import React, { Component } from 'react'
import { translate } from 'react-polyglot'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { Tooltip, Button, Icon, Label, Avatar } from 'components'
import ContextMenu from '../ContextMenu'

import './sortableTree.scss'

function isDescendant(older, younger) {
	return (
		!!older.children &&
		typeof older.children !== 'function' &&
		older.children.some(
			child => child === younger || isDescendant(child, younger)
		)
	)
}

// eslint-disable-next-line react/prefer-stateless-function
class FileThemeNodeContentRenderer extends Component {
	setVisibilityState = (id, visible) => {
		localStorage.setItem(`a${id}`, visible)
	}

	render() {
		const {
			scaffoldBlockPxWidth,
			toggleChildrenVisibility,
			connectDragPreview,
			connectDragSource,
			isDragging,
			canDrop,
			canDrag,
			node,
			title,
			draggedNode,
			path,
			treeIndex,
			isSearchMatch,
			isSearchFocus,
			icons,
			className,
			style,
			didDrop,
			lowerSiblingCounts,
			listIndex,
			swapFrom,
			swapLength,
			swapDepth,
			activeId, // SA
			isReadOnly, // SA
			rowDirection, // SA
			userId, // SA
			onRemove,
			onDuplicate,
			onAddSubEntry,
			onChangeArticleState,
			onChangeOutdatedState,
			node: { id },
			t,

			// Not needed, but preserved for other renderers
			treeId, // eslint-disable-line no-unused-vars

			// Not needed, but preserved for other renderers
			isOver, // eslint-disable-line no-unused-vars

			// Needed for dndManager
			parentNode, // eslint-disable-line no-unused-vars
			...otherProps
		} = this.props
		const nodeTitle = title || node.title
		const isDraggedDescendant =
			draggedNode && isDescendant(draggedNode, node)
		const isLandingPadActive = !didDrop && isDragging

		// Construct the scaffold representing the structure of the tree
		// lowerSiblingCounts.forEach((lowerSiblingCount, i) => {
		//   if (treeIndex !== listIndex && i === swapDepth) {
		//     // This row has been shifted, and is at the depth of
		//     // the line pointing to the new destination
		//     let highlightLineClass = ''

		//     if (listIndex === swapFrom + swapLength - 1) {
		//       // This block is on the bottom (target) line
		//       // This block points at the target block (where the row will go when released)
		//       highlightLineClass = 'o-sortable-tree__highlightBottomLeftCorner'
		//     } else if (treeIndex === swapFrom) {
		//       // This block is on the top (source) line
		//       highlightLineClass = 'o-sortable-tree__highlightTopLeftCorner'
		//     } else {
		//       // This block is between the bottom and top
		//       highlightLineClass = 'o-sortable-tree__highlightLineVertical'
		//     }
		//   }
		// })

		const lockedBy =
			node.lockedBy && node.lockedBy.id !== userId ? node.lockedBy : false
		const initials =
			lockedBy && lockedBy.firstName && lockedBy.lastName
				? lockedBy.firstName.charAt(0) + lockedBy.lastName.charAt(0)
				: 'n/a'
		const name =
			lockedBy &&
			lockedBy.firstName &&
			lockedBy.lastName &&
			`${lockedBy.firstName} ${lockedBy.lastName}`
		const draft = node.draft || false
		const outdated = node.outdated || false

		const nodeContent = (
			<div style={{ height: '100%' }} {...otherProps}>
				<div
					className={
						(!canDrag
							? 'o-sortable-tree__row o-sortable-tree__row--drag-disabled'
							: 'o-sortable-tree__row') +
						(className ? ` ${className}` : '')
					}
				>
					{toggleChildrenVisibility &&
						node.children &&
						node.children.length > 0 && (
							<div
								className={
									node.expanded
										? 'o-sortable-tree__toggle o-sortable-tree__toggle--collapse'
										: 'o-sortable-tree__toggle o-sortable-tree__toggle--expand'
								}
							>
								<Button
									aria-label={
										node.expanded ? 'Collapse' : 'Expand'
									}
									icon={
										<Icon
											name={
												node.expanded
													? 'arrow-down'
													: 'arrow-right'
											}
										/>
									}
									onClick={() => {
										this.setVisibilityState(
											node.id,
											!node.expanded
										)

										toggleChildrenVisibility({
											node,
											path,
											treeIndex,
										})
									}}
								/>
							</div>
						)}

					<a
						href={node.id}
						className="o-sortable-tree__inner-wrap"
						role="menuitem"
						onClick={e => {
							e.preventDefault()
						}}
					>
						{/* Set the row preview to be used during drag and drop */}
						{connectDragPreview(
							<div
								className={classNames(
									className,
									'o-sortable-tree__inner',
									{
										'o-sortable-tree__inner--landing-pad': isLandingPadActive,
									},
									{
										'o-sortable-tree__inner--cancel-pad':
											isLandingPadActive && !canDrop,
									},
									{
										'o-sortable-tree__inner--spacing-right':
											!lockedBy && !draft,
									}
								)}
							>
								{nodeTitle.length > 25 ? (
									<Tooltip title={nodeTitle}>
										<div className="o-sortable-tree__label">
											{nodeTitle}
										</div>
									</Tooltip>
								) : (
									<div className="o-sortable-tree__label">
										{nodeTitle}
									</div>
								)}
							</div>
						)}
					</a>

					{draft && (
						<Tooltip title={t('status.draft')}>
							<div className="o-sortable-tree__state">
								<Icon name="draft" />
							</div>
						</Tooltip>
					)}

					{lockedBy && (
						<Tooltip title={t('status.locked')}>
							<div className="o-sortable-tree__state">
								<Icon name="lock" />
							</div>
						</Tooltip>
					)}

					<div className="o-sortable-tree__actions">
						{lockedBy ? (
							<Tooltip
								title={`${t(
									'status.locked_long'
								)} ${t('status.locked_text', { name })}`}
							>
								<Avatar
									initials={initials}
									image={lockedBy.image}
									size="small"
								/>
							</Tooltip>
						) : (
							<>
								{this.props.isReadOnly ? null : (
									<ContextMenu
										onRemove={onRemove}
										onDuplicate={onDuplicate}
										onChangeArticleState={
											onChangeArticleState
										}
										onChangeOutdatedState={
											onChangeOutdatedState
										}
										onAddSubEntry={onAddSubEntry}
										id={id}
										draft={draft}
										outdated={outdated}
										allowSubEntry={
											this.props.path.length < 3
										}
									/>
								)}
								<div className="o-sortable-tree__active">
									<Icon name="dot" />
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		)

		return canDrag
			? connectDragSource(nodeContent, { dropEffect: 'copy' })
			: nodeContent
	}
}

FileThemeNodeContentRenderer.defaultProps = {
	canDrag: false,
	canDrop: false,
	className: '',
	draggedNode: null,
	icons: [],
	isSearchFocus: false,
	isSearchMatch: false,
	parentNode: null,
	style: {},
	swapDepth: null,
	swapFrom: null,
	swapLength: null,
	title: null,
	toggleChildrenVisibility: null,
}

FileThemeNodeContentRenderer.propTypes = {
	canDrag: PropTypes.bool,
	className: PropTypes.string,
	icons: PropTypes.arrayOf(PropTypes.node),
	isSearchFocus: PropTypes.bool,
	isSearchMatch: PropTypes.bool,
	listIndex: PropTypes.number.isRequired,
	lowerSiblingCounts: PropTypes.arrayOf(PropTypes.number).isRequired,
	node: PropTypes.shape({}).isRequired,
	path: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	).isRequired,
	scaffoldBlockPxWidth: PropTypes.number,
	style: PropTypes.shape({}),
	swapDepth: PropTypes.number,
	swapFrom: PropTypes.number,
	swapLength: PropTypes.number,
	title: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	toggleChildrenVisibility: PropTypes.func,
	treeIndex: PropTypes.number.isRequired,
	treeId: PropTypes.string.isRequired,

	// Drag and drop API functions
	// Drag source
	connectDragPreview: PropTypes.func.isRequired,
	connectDragSource: PropTypes.func.isRequired,
	didDrop: PropTypes.bool.isRequired,
	draggedNode: PropTypes.shape({}),
	isDragging: PropTypes.bool.isRequired,
	parentNode: PropTypes.shape({}), // Needed for dndManager
	// Drop target
	canDrop: PropTypes.bool,
	isOver: PropTypes.bool.isRequired,
}

export default translate()(FileThemeNodeContentRenderer)
